import type { ChangeEvent } from 'react'
import { Plus, X } from 'lucide-react'
import type { SendCrewInvite } from '@/modules/auth/use-cases'
import { Button, Input, Label } from '@/shared/ui/primitives'
import { CrewHeroCard } from './crew-hero-card'
import { useCrewInviteStep } from './use-crew-invite-step'
import type { CrewInviteField, CrewInviteRow } from './use-crew-invite-step'

export type CrewInviteStepProps = {
  orgId: string
  onSendCrewInvite: SendCrewInvite
  onDone: () => void
  fullBleed?: boolean
}

export function CrewInviteStep(props: CrewInviteStepProps) {
  const vm = useCrewInviteStep(props)
  const canRemove = vm.rows.length > 1

  return (
    <CrewHeroCard
      testId="step-crewInvite"
      title="Invite your crew"
      subtitle="Add the hunters you hunt with. Each one gets an email invite, and anyone without BlindBook Pro is asked to upgrade when they join."
      fullBleed={props.fullBleed}
    >
      <form
        onSubmit={vm.handleSend}
        className="flex flex-col gap-5"
        data-testid="crew-invite-form"
        noValidate
      >
        {vm.sentCount > 0 ? (
          <p data-testid="crew-invite-sent" className="text-sm font-medium text-primary">
            {vm.sentCount === 1 ? '1 invite sent.' : `${vm.sentCount} invites sent.`} Fix the
            ones below or tap Done.
          </p>
        ) : null}

        {vm.rows.map((row, index) => (
          <InviteRowFields
            key={row.id}
            row={row}
            index={index}
            canRemove={canRemove}
            disabled={vm.isSending}
            onChange={vm.updateRow}
            onRemove={vm.removeRow}
          />
        ))}

        <Button
          type="button"
          variant="outline"
          onClick={vm.addRow}
          disabled={vm.isSending}
          className="w-full"
          data-testid="crew-invite-add"
        >
          <Plus className="size-4" />
          Add another hunter
        </Button>

        {vm.formError ? (
          <p role="alert" className="text-sm text-destructive">
            {vm.formError}
          </p>
        ) : null}

        <div className="flex flex-col gap-2">
          <Button
            type="submit"
            disabled={vm.isSending}
            className="h-auto w-full rounded-lg py-3.5 text-base font-semibold"
            data-testid="crew-invite-send"
          >
            {vm.isSending ? 'Sending…' : 'Send invites'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={vm.handleSkip}
            disabled={vm.isSending}
            className="w-full"
            data-testid="crew-invite-skip"
          >
            {vm.sentCount > 0 ? 'Done' : 'Skip for now'}
          </Button>
          <p data-testid="crew-invite-later" className="text-center text-xs text-muted-foreground">
            You can invite more hunters anytime from the menu → Crew Roster.
          </p>
        </div>
      </form>
    </CrewHeroCard>
  )
}

type InviteRowFieldsProps = {
  row: CrewInviteRow
  index: number
  canRemove: boolean
  disabled: boolean
  onChange: (id: number, field: CrewInviteField, value: string) => void
  onRemove: (id: number) => void
}

function InviteRowFields({ row, index, canRemove, disabled, onChange, onRemove }: InviteRowFieldsProps) {
  const prefix = `crew-invite-${row.id}`
  const hunterLabel = `Hunter ${index + 1}`
  return (
    <fieldset className="flex flex-col gap-3" data-testid="crew-invite-row" aria-label={hunterLabel}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary">{hunterLabel}</p>
        {canRemove ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() => onRemove(row.id)}
            disabled={disabled}
            aria-label={`Remove ${hunterLabel}`}
          >
            <X className="size-4" />
          </Button>
        ) : null}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${prefix}-first`}>First name</Label>
          <Input
            id={`${prefix}-first`}
            value={row.firstName}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(row.id, 'firstName', e.target.value)}
            disabled={disabled}
            autoComplete="off"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${prefix}-last`}>Last name</Label>
          <Input
            id={`${prefix}-last`}
            value={row.lastName}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(row.id, 'lastName', e.target.value)}
            disabled={disabled}
            autoComplete="off"
          />
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor={`${prefix}-email`}>Email</Label>
        <Input
          id={`${prefix}-email`}
          type="email"
          inputMode="email"
          value={row.email}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(row.id, 'email', e.target.value)}
          disabled={disabled}
          autoComplete="off"
          autoCapitalize="none"
        />
      </div>
      {row.error ? (
        <p role="alert" className="text-xs text-destructive">
          {row.error}
        </p>
      ) : null}
    </fieldset>
  )
}
